<template>
  <div
    :id="id"
    :class="bem()"
  >
    <TitleBlock
      v-if="block.titleBlock"
      :class="bem('title')"
      :title="block.titleBlock"
    />
    <Swiper
      v-bind="swiperOptions"
      :class="bem('items')"
      @swiper="onSwiper"
    >
      <SwiperSlide
        v-for="(item, index) in block.items"
        v-slot="{ isActive }"
        :key="item.id"
        :class="bem('slide')"
      >
        <CenteredCarouselItem
          :item="item"
          :index="index"
          :parent-tracking-id="block.trackingId"
          :active="isActive"
        />
      </SwiperSlide>
    </Swiper>
    <div :class="bem('controls')">
      <CarouselArrow
        arrow="left"
      />
      <CarouselArrow
        arrow="right"
      />
    </div>
  </div>
</template>

<script setup>
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Navigation, Mousewheel, EffectCoverflow } from 'swiper/modules'

const bem = useBEM('CenteredCarouselRow')

const props = defineProps({
  block: {type: Object, required: true},
  rowPosition: {type: Number, default: 0}
})

const useSwiper = ref(null)
const id = ref('carousel-row--' + props.block.id)
const modules = [Navigation, Mousewheel, EffectCoverflow]
const totalItems = props.block.items.length
const desktopLoop = 4.25
const tabletLoop = 2.5
const mobileLoop = 1.75
const isLoopedDesktop = (totalItems >= (desktopLoop * 2))
const isLoopedTablet = (totalItems >= (tabletLoop * 2))
const isLoopedMobile = (totalItems >= (mobileLoop * 2))


// Swiper will only be centered with
// enough items to make it loop
const swiperOptions = ref({
  modules: modules,
  slidesPerView: mobileLoop,
  spaceBetween: 0,
  speed: 300,
  centeredSlides: isLoopedMobile,
  loop: isLoopedMobile,
  loopAdditionalSlides: totalItems,
  slideToClickedSlide: true,
  effect: 'coverflow',
  coverflowEffect: {
    rotate: 0,
    stretch: 0,
    depth: 80,
    modifier: 1,
    rotate: 0,
    slideShadows: false,
  },
  mousewheel: {
    enabled: true,
    forceToAxis: true,
    sensitivity: 0.2
  },
  breakpoints: {
    650: {
      slidesPerView: tabletLoop,
      centeredSlides: isLoopedTablet,
      loop: isLoopedTablet,
    },
    1150: {
      slidesPerView: 4,
      centeredSlides: isLoopedDesktop,
      loop: isLoopedDesktop,
      speed: 500,
      coverflowEffect: {
        depth: 80,
      },
    },
    1500: {
      slidesPerView: desktopLoop,
      coverflowEffect: {
        depth: 85,
      },
    }
  },
  navigation: {
    nextEl: `#${id.value} [data-carousel-arrow="right"]`,
    prevEl: `#${id.value} [data-carousel-arrow="left"]`,
  },
});


const onSwiper = (swiper) => {
  useSwiper.value = swiper
};
</script>

<style lang="postcss">
.CenteredCarouselRow {
  @apply w-full relative;
  
  @screen xxl {
    @apply global-wrapper;
  }
}

.CenteredCarouselRow__title {
  @apply text-center mb-10;
}

.CenteredCarouselRow__controls {
  @apply absolute inset-0  pointer-events-none
  flex justify-between items-center z-[2]
  px-[calc(var(--outer-gutter)_/_2_-_16px)]
  mdMax:hidden;

  @screen full {
    @apply px-3
  }
}

.CenteredCarouselRow__items,
.swiper-container.Carousel__items {
  @apply w-screen xxl:w-full;

  .swiper-wrapper {
    @apply flex flex-row w-full;
  }
  
  @screen xxl {
    margin-left: 0;
    margin-right: 0;
  }
}

.swiper-container.CenteredCarouselRow__items {
  @apply flex overflow-hidden;
}

.swiper-slide.CenteredCarouselRow__slide {
  @apply w-[57%] sm:w-[40%] xl:w-[25%] xxl:w-[23.5%] shrink-0;
}
</style>
